import { useState } from 'react'
import { api } from '../lib/api'
import { getCachedUser } from '../lib/auth'
import type { Person } from '../lib/types'

/**
 * "Is this you?" (v0.20.0, D-119).
 *
 * The measured problem: 2 of 9 members had claimed their own record, so the
 * relationship-finder — which needs a claim to know where "you" are — was invisible
 * to almost everyone, and a tree of 24 names had nobody marked as the reader.
 *
 * Matching happens CLIENT-SIDE against the graph already in memory: no new endpoint,
 * no new attack surface, and nothing is revealed that the viewer cannot already see.
 * A hit is only ever a SUGGESTION — confirming files a normal claim, which the keeper
 * still approves (the identity model is deliberately never automatic).
 */

/** Names in this family live in the email local part ("sareh1999", "taherrasheed86",
 *  "rasheed.yahya"), so exact matching finds nothing — strip digits/separators and
 *  look for the first name at the start, or both names anywhere. */
export function findMeCandidates(persons: Person[], name?: string, email?: string): Person[] {
  const clean = (s: string) => s.toLowerCase().replace(/[^a-z]/g, '')
  const local = clean((email || '').split('@')[0] || '')
  const display = clean(name || '')
  if (!local && !display) return []

  const scored: { p: Person; score: number }[] = []
  for (const p of persons) {
    if (p.claimed_by) continue
    const first = clean(p.first_name || '')
    const last = clean(p.last_name || '')
    if (first.length < 3) continue
    let score = 0
    if (display && display.startsWith(first)) score = 100          // display name wins
    else if (display && last && display.includes(first) && display.includes(last)) score = 90
    else if (local.startsWith(first)) score = 80                    // sareh1999 → Sareh
    else if (last && local.includes(first) && local.includes(last)) score = 70  // rasheed.yahya → Yahya Rasheed
    if (score) scored.push({ p, score: score + Math.min(first.length, 9) })
  }
  return scored.sort((a, b) => b.score - a.score).slice(0, 3).map(s => s.p)
}

export default function ClaimPrompt({ treeId, persons, onFiled }: {
  treeId: string; persons: Person[]; onFiled?: () => void
}) {
  const me = getCachedUser()
  const dismissKey = `fa_claim_prompt_${treeId}`
  const [hidden, setHidden] = useState(() => localStorage.getItem(dismissKey) === '1')
  const [msg, setMsg] = useState('')
  const [busy, setBusy] = useState(false)

  // already anchored in this tree? then nothing to ask
  const alreadyMine = persons.some(p => p.claimed_by && p.claimed_by === me?.id)
  const candidates = alreadyMine ? [] : findMeCandidates(persons, me?.name, me?.email)
  if (hidden || alreadyMine || !candidates.length) return null

  const claim = async (personId: string) => {
    setBusy(true)
    try {
      const r = await api.fileClaim({ tree_id: treeId, kind: 'claim', person_id: personId })
      setMsg(r.message || 'Sent to the keeper — once they confirm, the tree knows where you are.')
      localStorage.setItem(dismissKey, '1')
      onFiled?.()
    } catch (e) {
      setMsg(e instanceof Error && e.message.includes('409')
        ? 'You already asked about this one.'
        : 'Could not send that just now.')
    } finally { setBusy(false) }
  }

  return (
    <div className="card-glass p-4 mb-4 border-rootv-500/30 fade-up">
      <h3 className="font-display text-base font-semibold text-slate-100 mb-1">Which one of these is you?</h3>
      <p className="text-xs text-slate-500 mb-3">
        Point at yourself and the tree can show how everyone else is related to <b>you</b> — parents, cousins,
        the exact word for it in Arabic. The keeper confirms it.
      </p>
      <div className="flex flex-wrap gap-2">
        {candidates.map(p => (
          <button key={p.id} onClick={() => claim(p.id)} disabled={busy}
            className="text-sm px-3 py-1.5 rounded-lg border border-gold-500/35 text-gold-200 hover:bg-gold-500/10 transition-colors">
            ✋ {p.first_name} {p.last_name}
            {p.birth_date ? <span className="text-slate-500 text-xs"> · b.{String(p.birth_date).slice(0, 4)}</span> : null}
          </button>
        ))}
        <button onClick={() => { localStorage.setItem(dismissKey, '1'); setHidden(true) }}
          className="text-sm px-3 py-1.5 rounded-lg text-slate-500 hover:text-slate-300">
          none of these
        </button>
      </div>
      {msg && <p className="text-xs text-gold-300 mt-3">{msg}</p>}
    </div>
  )
}
